
export default function UXSolutions() {
  const solutions = [
    {
      title: 'Smart Payment Hub',
      description: 'One-tap payments with auto-pay, payment plans, and real-time balance tracking. Supports ACH, cards, and UPI for global markets.',
      icon: 'ri-bank-card-2-line',
      color: '#1FA372',
      bgColor: '#F0FDF9',
      features: ['Auto-pay scheduling', 'Payment reminders', 'Split payments', 'Receipt generation']
    },
    {
      title: 'Maintenance Command Center',
      description: 'AI-powered request routing, vendor matching, and real-time tracking from submission to completion.',
      icon: 'ri-tools-line',
      color: '#F97316',
      bgColor: '#FFF7ED',
      features: ['Photo/video uploads', 'Priority scoring', 'Vendor ratings', 'SLA tracking']
    },
    {
      title: 'Community Hub',
      description: 'Centralized communication platform with announcements, polls, event management, and neighbor connections.',
      icon: 'ri-community-line',
      color: '#3B82F6',
      bgColor: '#EFF6FF',
      features: ['Push notifications', 'Event calendar', 'Discussion forums', 'Emergency alerts']
    },
    {
      title: 'Document Vault',
      description: 'Secure, searchable repository for CC&Rs, meeting minutes, financial reports, and community guidelines.',
      icon: 'ri-folder-shield-2-line',
      color: '#8B5CF6',
      bgColor: '#F5F3FF',
      features: ['Version control', 'E-signatures', 'Search & filter', 'Access controls']
    },
    {
      title: 'Violation Manager',
      description: 'Streamlined violation reporting, tracking, and resolution with automated notifications and appeal workflows.',
      icon: 'ri-error-warning-line',
      color: '#EF4444',
      bgColor: '#FEF2F2',
      features: ['Photo evidence', 'Appeal process', 'Fine tracking', 'Compliance history']
    },
    {
      title: 'Board Portal',
      description: 'Dedicated workspace for board members with meeting management, voting, and financial oversight tools.',
      icon: 'ri-government-line',
      color: '#14B8A6',
      bgColor: '#F0FDFA',
      features: ['Meeting scheduler', 'Digital voting', 'Budget tools', 'Audit trails']
    }
  ];

  return (
    <section className="py-24 bg-[#FAFBFC]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#1FA372]/10 text-[#1FA372] rounded-full text-sm font-semibold mb-4 border border-[#1FA372]/20">
            07 — Solutions
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1E293B] mb-6">
            Key UX Solutions & Innovations
          </h2>
          <p className="text-[#64748B] text-lg max-w-3xl mx-auto">
            Feature innovations designed to solve real problems and deliver measurable value to every user type.
          </p>
        </div>

        {/* Solutions Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {solutions.map((solution, index) => (
            <div 
              key={index}
              className="bg-white rounded-3xl p-6 border border-[#E2E8F0] hover:shadow-lg transition-all group hover:-translate-y-1"
            >
              <div 
                className="w-14 h-14 flex items-center justify-center rounded-2xl mb-4 border"
                style={{ backgroundColor: solution.bgColor, borderColor: `${solution.color}30` }}
              >
                <i className={`${solution.icon} text-2xl`} style={{ color: solution.color }}></i>
              </div>

              <h3 className="text-xl font-bold text-[#1E293B] mb-3">{solution.title}</h3>
              <p className="text-[#64748B] text-sm mb-4 leading-relaxed">{solution.description}</p>

              <div className="flex flex-wrap gap-2">
                {solution.features.map((feature, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1 bg-[#F1F5F9] text-[#64748B] text-xs rounded-full border border-[#E2E8F0]"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Feature Showcase */}
        <div className="bg-gradient-to-br from-[#F0FDF9] to-white rounded-3xl p-8 border border-[#1FA372]/20 shadow-sm">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold text-[#1E293B] mb-4">
                Unified Dashboard Experience
              </h3>
              <p className="text-[#64748B] mb-6 leading-relaxed">
                A single, intuitive dashboard that adapts to each user role. Residents see their dues and requests, managers see community-wide metrics, and board members access governance tools.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1FA372]/10 rounded-xl flex-shrink-0 border border-[#1FA372]/20">
                    <i className="ri-dashboard-3-line text-[#1FA372]"></i>
                  </div>
                  <div>
                    <h4 className="text-[#1E293B] font-semibold mb-1">Role-Adaptive Interface</h4>
                    <p className="text-[#64748B] text-sm">Dashboard widgets and navigation adjust based on user permissions and role.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#F97316]/10 rounded-xl flex-shrink-0 border border-[#F97316]/20">
                    <i className="ri-notification-3-line text-[#F97316]"></i>
                  </div>
                  <div>
                    <h4 className="text-[#1E293B] font-semibold mb-1">Smart Notifications</h4>
                    <p className="text-[#64748B] text-sm">AI-prioritized alerts ensure users never miss critical updates.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#3B82F6]/10 rounded-xl flex-shrink-0 border border-[#3B82F6]/20">
                    <i className="ri-bar-chart-grouped-line text-[#3B82F6]"></i>
                  </div>
                  <div>
                    <h4 className="text-[#1E293B] font-semibold mb-1">Real-Time Analytics</h4>
                    <p className="text-[#64748B] text-sm">Live data visualization for payments, requests, and community engagement.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#1FA372]/10 to-[#F97316]/10 rounded-3xl blur-xl"></div>
              <div className="relative bg-white rounded-2xl p-2 border border-[#E2E8F0] shadow-xl">
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Modern%20HOA%20management%20dashboard%20interface%20showing%20payment%20summary%20maintenance%20requests%20community%20announcements%20and%20quick%20actions%20with%20clean%20white%20light%20theme%20green%20accent%20colors%20clean%20enterprise%20SaaS%20design%20professional%20property%20management%20software%20UI&width=800&height=600&seq=hoa-dashboard-feature-light-001&orientation=landscape"
                    alt="Dashboard Feature Showcase"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile App Showcase */}
        <div className="mt-16 bg-white rounded-3xl p-8 border border-[#E2E8F0] shadow-sm">
          <h3 className="text-2xl font-bold text-[#1E293B] mb-8 text-center">Mobile-First Design</h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-[#F1F5F9] rounded-3xl p-4 mb-4 inline-block">
                <div className="w-48 h-96 rounded-2xl overflow-hidden mx-auto shadow-lg">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Mobile%20app%20screen%20showing%20HOA%20payment%20interface%20with%20balance%20due%20quick%20pay%20button%20and%20payment%20history%20clean%20white%20light%20theme%20with%20green%20accent%20modern%20fintech%20style%20clean%20UI%20design&width=300&height=600&seq=hoa-mobile-payment-light-001&orientation=portrait"
                    alt="Mobile Payment Screen"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <h4 className="text-[#1E293B] font-semibold mb-2">Quick Payments</h4>
              <p className="text-[#64748B] text-sm">Pay dues in under 10 seconds</p>
            </div>
            <div className="text-center">
              <div className="bg-[#F1F5F9] rounded-3xl p-4 mb-4 inline-block">
                <div className="w-48 h-96 rounded-2xl overflow-hidden mx-auto shadow-lg">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Mobile%20app%20screen%20showing%20maintenance%20request%20form%20with%20photo%20upload%20and%20category%20selection%20clean%20white%20light%20theme%20with%20orange%20accent%20modern%20enterprise%20app%20style%20clean%20UI%20design&width=300&height=600&seq=hoa-mobile-maintenance-light-001&orientation=portrait"
                    alt="Mobile Maintenance Screen"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <h4 className="text-[#1E293B] font-semibold mb-2">Easy Requests</h4>
              <p className="text-[#64748B] text-sm">Submit with photos in 3 taps</p>
            </div>
            <div className="text-center">
              <div className="bg-[#F1F5F9] rounded-3xl p-4 mb-4 inline-block">
                <div className="w-48 h-96 rounded-2xl overflow-hidden mx-auto shadow-lg">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Mobile%20app%20screen%20showing%20community%20announcements%20feed%20with%20event%20cards%20and%20notification%20badges%20clean%20white%20light%20theme%20with%20blue%20accent%20modern%20social%20app%20style%20clean%20UI%20design&width=300&height=600&seq=hoa-mobile-community-light-001&orientation=portrait"
                    alt="Mobile Community Screen"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <h4 className="text-[#1E293B] font-semibold mb-2">Stay Connected</h4>
              <p className="text-[#64748B] text-sm">Community updates at a glance</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
